import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import PaymentMethod from '../components/paymentMethod';
import BillingDetails from '../components/billingDetails/BillingDetails';
import { Headline } from '../components/headline';
import VatNumber from '../components/vatNumber/VatNumber';
import { PAYMENT_METHODS } from '../constants';

const StyledHeadline = styled(Headline)`
	margin-bottom: ${({ theme }) => theme.spacings.giga};
`;

const FormSection = styled.div`
	margin-top: ${({ theme }) => theme.spacings.tera};
`;

const NewPaymentDetails = observer(({
	customer,
	businessAccount,
	legalEntity,
	showCardZipCode,
	showBillingDetails,
	showVatNumber
}) => {
	const { t } = useTranslation();

	const onPaymentMethodChange = ({ paymentMethodType, paymentMethod }) => {
		if (paymentMethod.type === PAYMENT_METHODS.businessAccount) {
			return customer.setPaymentMethod(paymentMethodType, businessAccount);
		}
		return customer.setPaymentMethod(paymentMethodType, paymentMethod);
	};

	const multiplePaymentMethods = !!(customer.availablePaymentMethods.directDebit || customer.availablePaymentMethods.businessAccount || customer.availablePaymentMethods.suPayout || customer.availablePaymentMethods.suPaymentMethods || customer.availablePaymentMethods.suBusinessAccount);

	return (
		<>
			<StyledHeadline as="h2" size='four'>{
				multiplePaymentMethods
					? t('payment_method.title')
					: t('payment_method.credit_card.title')
			}</StyledHeadline>

			<PaymentMethod
				type={customer.paymentMethodType}
				legalEntities={customer.legalEntities}
				countryCode={customer.billingDetails.country}
				availablePaymentMethods={customer.availablePaymentMethods}
				businessAccountEnabled={businessAccount && businessAccount.enabled}
				showCardZipCode={showCardZipCode}
				legalEntity={legalEntity}
				onChange={onPaymentMethodChange}
			/>
			{showBillingDetails && (
				<FormSection>
					<StyledHeadline as="h3" size="four">{t('billing_details.title')}</StyledHeadline>
					<BillingDetails
						value={customer.billingDetails}
						onChange={(billingDetails) => customer.setBillingDetails(billingDetails)}
					/>
				</FormSection>
			)}
			{showVatNumber && (
				<FormSection>
					<StyledHeadline as="h3" size="four">{t('vat.title')}</StyledHeadline>
					<VatNumber
						value={customer.billingDetails.vatNumber}
						country={customer.billingDetails.country}
						onChange={(vatNumber) => customer.setVatNumber(vatNumber)}
					/>
				</FormSection>
			)}
		</>
	);
});

NewPaymentDetails.propTypes = {
	showBillingDetails: PropTypes.bool,
	showVatNumber: PropTypes.bool,
	customer: PropTypes.shape({
		setBillingDetails: PropTypes.func.isRequired,
		setVatNumber: PropTypes.func.isRequired,
		billingDetails: PropTypes.object,
		legalEntities: PropTypes.array,
	}),
	businessAccount: PropTypes.object,
	legalEntity: PropTypes.string,
};

export default NewPaymentDetails;
